import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";

import { LeadService, Lead, LeadRequest } from "./lead.service";
import LeadForm from "./leadForm";
import { LeadFormDataProvider } from "./leadFormDataContext";
import { useDispatch, useSelector } from "react-redux";
import { setActivityTogglePopup } from "../../../core/data/redux/commonSlice";
import { all_routes } from "../../router/all_routes";
import ManageColumns from "../../../components/filters/ManageColumns";
import { useLeads } from "./useLeads";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import { useTenantChange } from "../../../hooks/useTenantChange";
import {
  getLifecycleTypePluralName,
  getLifecycleTypeName,
} from "../../../utils/lifecycleUtils";
import { AuthContext } from "../../../context/authcontext";
import male from "../../../../src/assets/male.png";
import TableFilters, {
  FilterState,
  FilterConfig,
  mergeTableFilterChanges,
} from "../../../components/filters/TableFilters";
import SortableHeader from "../../../components/filters/SortableHeader";
import SortByDropdown from "../../../components/filters/SortByDropdown";
import ListFilterChips, {
  getClearedListFilters,
} from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import { getStageColor } from "../../../utils/stageColors";
import { sortRecords } from "../../../utils/sortUtils";
import ExportComponent from "../../../components/ExportComponent";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import { languageOptions } from "../../../components/language";
import EntityDetailsModal, {
  EntityDetailSection,
  PriorityBadge,
} from "../../../components/EntityDetailsModal/EntityDetailsModal";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import { openOffcanvas } from "../../../utils/offcanvasUtils";

interface LeadListProps {
  onViewSwitch?: (view: "list" | "kanban") => void;
}

const LeadList = ({ onViewSwitch }: LeadListProps) => {
  const route = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedTenantId } = useContext(AuthContext);
  const {
    leads,
    loading,
    backgroundLoading,
    error,
    pagination,
    addLead,
    updateLead,
    removeLead,
    fetchLeads,
    isInitialized,
    clearBackgroundLoading,
    clearLeadsData,
  } = useLeads();
  const [tenantLoading, setTenantLoading] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [isUpdatingLead, setIsUpdatingLead] = useState(false);

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [detailsLead, setDetailsLead] = useState<Lead | null>(null);
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
    archivedFilter: "no",
  });

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const defaultColumnVisibility = {
    title: true,
    company: true,
    estimated_value: true,
    assigned_team: true,
    assigned_user: true,
    lifecycle_type_stage: true,
    source: false,
    preferred_language: false,
    time_zone: false,
    currency_code: false,
    priority: false,
    primary_contact: false,
  };

  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>(() => {
    try {
      const saved = localStorage.getItem("leads_column_visibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        const visibility: { [key: string]: boolean } = {
          ...defaultColumnVisibility,
        };
        Object.keys(parsed).forEach((key) => {
          if (key in defaultColumnVisibility) {
            visibility[key] = parsed[key] === true;
          }
        });
        const visibleCount = Object.values(visibility).filter(Boolean).length;
        if (visibleCount === 0) {
          const defaultKeys = Object.keys(defaultColumnVisibility);
          for (const key of defaultKeys) {
            if (
              defaultColumnVisibility[
              key as keyof typeof defaultColumnVisibility
              ]
            ) {
              visibility[key] = true;
              break;
            }
          }
        }
        return visibility;
      }
    } catch (error) {
      console.error(
        "Error loading column visibility from localStorage:",
        error
      );
    }
    return defaultColumnVisibility;
  });

  const saveColumnVisibilityToStorage = useCallback(
    (visibility: { [key: string]: boolean }) => {
      try {
        localStorage.setItem(
          "leads_column_visibility",
          JSON.stringify(visibility)
        );
      } catch (error) {
        console.error(
          "Error saving column visibility to localStorage:",
          error
        );
      }
    },
    []
  );

  const availableColumns = [
    { key: "title", label: "Title" },
    { key: "company", label: "Company" },
    { key: "estimated_value", label: "Estimated Value" },
    { key: "assigned_team", label: "Assigned Team" },
    { key: "assigned_user", label: "Assigned User" },
    { key: "lifecycle_type_stage", label: "Status" },
    { key: "source", label: "Source" },
    { key: "preferred_language", label: "Preferred Language" },
    { key: "time_zone", label: "Time Zone" },
    { key: "currency_code", label: "Currency" },
    { key: "priority", label: "Priority" },
    { key: "primary_contact", label: "Contact Name" },
  ];

  const [orderedAvailableColumns, setOrderedAvailableColumns] = useState(() => {
    const storageKey = `${window.location.pathname}_column_order`;
    const savedOrder = localStorage.getItem(storageKey);
    if (savedOrder) {
      try {
        const orderKeys = JSON.parse(savedOrder);
        return [...availableColumns].sort((a, b) => {
          const indexA = orderKeys.indexOf(a.key);
          const indexB = orderKeys.indexOf(b.key);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } catch (e) {
        return [...availableColumns];
      }
    }
    return [...availableColumns];
  });

  const getSortedAvailableColumns = () => {
    return orderedAvailableColumns;
  };

  const handleColumnVisibilityChange = (
    columnKey: string,
    isVisible: boolean
  ) => {
    const currentVisibleCount =
      Object.values(columnVisibility).filter(Boolean).length;
    if (isVisible && currentVisibleCount >= 6) {
      showErrorAlert(
        "Maximum 6 columns can be enabled at once. Please disable another column first."
      );
      return;
    }
    if (!isVisible && currentVisibleCount <= 1) {
      showErrorAlert("At least one column must remain visible.");
      return;
    }
    const newColumnVisibility = {
      ...columnVisibility,
      [columnKey]: isVisible,
    };
    setColumnVisibility(newColumnVisibility);
    saveColumnVisibilityToStorage(newColumnVisibility);
  };

  const handleResetColumns = () => {
    setColumnVisibility(defaultColumnVisibility);
    saveColumnVisibilityToStorage(defaultColumnVisibility);
  };

  useTenantChange({
    onTenantChange: () => {
      clearLeadsData();
      setCurrentPage(1);
      setSelectedItems([]);
      setSelectAll(false);
    },
    refreshData: () => fetchLeads(1, 10),
    clearData: clearLeadsData,
  });

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "title",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false, // Keep status filter in dropdown on right side
      pageName: "lead" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      const mergedFilters = mergeTableFilterChanges(filters, newFilters);
      setFilters(mergedFilters);
      setCurrentPage(1);
      fetchLeads(1, 12, mergedFilters);
    },
    [fetchLeads, filters]
  );

  // Right side filter dropdown change handler
  const handleDropdownFilterChange = useCallback(
    (dropdownFilters: Record<string, any>) => {
      const updatedFilters = {
        ...filters,
        ...dropdownFilters,
      };
      setFilters(updatedFilters);
      setCurrentPage(1);
      fetchLeads(1, 12, updatedFilters);
    },
    [filters, fetchLeads]
  );

  const applyListFilters = useCallback(
    (nextFilters: FilterState) => {
      setFilters(nextFilters);
      setCurrentPage(1);
      fetchLeads(1, 12, nextFilters);
    },
    [fetchLeads]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchLeads(page, 12, filters);
  };

  const handleSortChange = useCallback(
    (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
    },
    [filters]
  );

  const paginatedLeads = useMemo(
    () =>
      sortRecords(
        Array.isArray(leads) ? leads : [],
        filters.sortBy,
        filters.sortOrder
      ),
    [leads, filters.sortBy, filters.sortOrder]
  );

  const activityToggle = useSelector(
    (state: any) => state?.activityTogglePopup
  );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = paginatedLeads
        .filter((item) => item.lead_id)
        .map((item) => item.lead_id as number);
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (leadId: number) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId];

      const validCurrentData = paginatedLeads.filter((item) => item.lead_id);
      setSelectAll(
        newSelected.length === validCurrentData.length &&
        validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  useEffect(() => {
    if (paginatedLeads.length > 0) {
      const validCurrentData = paginatedLeads.filter((item) => item.lead_id);
      const allCurrentSelected = validCurrentData.every(
        (item) => item.lead_id && selectedItems.includes(item.lead_id)
      );
      setSelectAll(allCurrentSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [paginatedLeads, selectedItems]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialized) {
      fetchLeads(1, 12);
    }
  }, [isInitialized, fetchLeads]);

  useEffect(() => {
    if (isInitialized && backgroundLoading) {
      const fetchBackgroundData = async () => {
        try {
        } catch (error) {
          console.error("Background API error:", error);
        } finally {
          clearBackgroundLoading();
        }
      };

      fetchBackgroundData();
    }
  }, [isInitialized, backgroundLoading, clearBackgroundLoading]);

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage);
    }
  }, [pagination.currentPage, currentPage]);

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit_lead");

    const handleHidden = () => {
      setSelectedLead(null);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    const addOffcanvas = document.getElementById("offcanvas_add_lead");

    const handleHidden = () => {
      setIsAddLeadOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    saveColumnVisibilityToStorage(columnVisibility);
  }, [columnVisibility, saveColumnVisibilityToStorage]);

  const handleCreate = async (lead: Partial<LeadRequest>) => {
    setIsCreatingLead(true);
    try {
      const newLead = await LeadService.createLead(lead);
      addLead(newLead);

      setSelectedLead(null);
      closeOffcanvas("offcanvas_add_lead");

      setTimeout(() => {
        showSuccessAlert(`Lead "${newLead.title}" created successfully!`);
      }, 500);
    } catch (err) {
      console.error("Failed to create lead:", err);
      showErrorAlert("Failed to create lead. Please try again.");
      throw err;
    } finally {
      setIsCreatingLead(false);
    }
  };

  const handleUpdate = async (id: number, lead: Partial<LeadRequest>) => {
    setIsUpdatingLead(true);
    try {
      const updatedLead = await LeadService.updateLead(id, lead);
      updateLead(updatedLead);

      closeOffcanvas("offcanvas_edit_lead");

      setTimeout(() => {
        showSuccessAlert(`Lead "${updatedLead.title}" updated successfully!`);
      }, 500);
    } catch (err) {
      console.error("Failed to update lead:", err);
      showErrorAlert("Failed to update lead. Please try again.");
      throw err;
    } finally {
      setIsUpdatingLead(false);
    }
  };

  const handleDelete = async (lead: Lead) => {
    const result = await showConfirmationAlert(
      "Delete Lead",
      `Are you sure you want to delete "${lead.title}"? This action cannot be undone.`,
      async () => {
        if (lead.lead_id) {
          await LeadService.deleteLead(lead.lead_id);
        }
      }
    );

    if (result.isConfirmed) {
      if (lead.lead_id) {
        removeLead(lead.lead_id);
      }
      await showSuccessAlert(`Lead "${lead.title}" deleted successfully!`);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    const result = await showConfirmationAlert(
      "Delete Selected Records",
      `Are you sure you want to delete ${selectedItems.length} selected record${selectedItems.length > 1 ? "s" : ""}? This action cannot be undone.`,
      async () => {
        setIsBulkDeleting(true);
        try {
          const deletePromises = selectedItems.map((leadId) =>
            LeadService.deleteLead(leadId)
          );
          await Promise.all(deletePromises);
        } finally {
          setIsBulkDeleting(false);
        }
      }
    );

    if (result.isConfirmed && result.value) {
      selectedItems.forEach((leadId) => removeLead(leadId));

      setSelectedItems([]);
      setSelectAll(false);

      await showSuccessAlert(
        `${selectedItems.length} lead${selectedItems.length > 1 ? "s" : ""
        } deleted successfully!`
      );
    }
  };;

  const handlePreview = (lead: Lead) => {
    navigate(`/leads/${lead.lead_id}`, {
      state: { leadData: lead },
    });
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector('[data-bs-dismiss="offcanvas"]') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(modal);
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      }
    }
  };

  const allAvailableColumns = useMemo(
    () => [
      {
        title: (
          <SortableHeader
            title={`${getLifecycleTypeName("LEAD")} Title`}
            field="title"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "title",
        key: "title",
        render: (text: string, record: Lead) => {
          const isActiveValue = (record as any).is_active;
          const inactiveReason = (record as any).lifecycle_status_reason?.reason_name;
          const showDot = isActiveValue !== null && isActiveValue !== undefined;
          const dot = showDot ? (
            isActiveValue === true ? (
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#198754",
                  flexShrink: 0,
                }}
                title="Active"
              />
            ) : (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`inactive-dot-${record.lead_id}`}>
                    {inactiveReason || "Marked as Inactive"}
                  </Tooltip>
                }
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#dc3545",
                    flexShrink: 0,
                    cursor: "default",
                  }}
                />
              </OverlayTrigger>
            )
          ) : null;
          return (
            <h2 className="d-flex align-items-center gap-2">
              {dot}
              <Link
                to={`/leads/${record.lead_id}`}
                state={{ leadData: record }}
                className="d-flex flex-column"
              >
                {record.title?.length > 40 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${record.lead_id}`}>{record.title}</Tooltip>}
                  >
                    <span>{`${record.title.substring(0, 40)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.title
                )}
                <span className="text-default">{record.lifecycle_source?.lifecycle_source_display_name || record.source}</span>
              </Link>
            </h2>
          );
        },
      },
      {
        title: (
          <SortableHeader
            title="Company"
            field="company"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "company",
        key: "company",
        render: (company: any, record: Lead) => (
          <div className={!company?.company_name ? "text-center fw-bold" : ""}>{company?.company_name || "-"}</div>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Estimated Value"
            field="estimated_value"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "estimated_value",
        key: "estimated_value",
        render: (value: number, record: Lead) => (
          <div className={!value ? "text-center fw-bold" : ""}>
            {value
              ? `${record.currency_code || "$"} ${Number(value || 0).toFixed(4)}`
              : "-"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned Team"
              field="assigned_team"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_team",
        key: "assigned_team",
        render: (assignedTeam: any, record: Lead) => (
          <div>
            {assignedTeam?.team_name || assignedTeam?.name || "No Team Assigned"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned User"
              field="assigned_user"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_user",
        key: "assigned_user",
        render: (assignedUser: any, record: Lead) => (
          <div className="d-flex align-items-center">
            <img
              src={assignedUser?.avatar_url || male}
              alt={assignedUser?.full_name || "Default Avatar"}
              className="me-2 rounded-circle"
              style={{ width: 24, height: 24, objectFit: "cover" }}
            />
            <span>{assignedUser?.full_name || "No User Assigned"}</span>
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("LEAD")} Status`}
              field="lifecycle_type_stage"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "lifecycle_type_stage",
        key: "lifecycle_type_stage",
        render: (lifecycleStage: any, record: Lead) => (
          <span
            className={`badge badge-pill badge-status ${getStageColor(
              record.lifecycle_type_stage?.stage_name || "",
              record.lifecycle_type_stage?.lifecycle_type_stage_id,
              (record.lifecycle_type_stage as any)?.stage_order
            )} no-border`}
            style={{ border: "none", borderColor: "transparent" }}
          >
            {lifecycleStage?.stage_name || "-"}
          </span>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Source"
            field="source"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "source",
        key: "source",
        render: (text: string, record: Lead) => (
          <div className={!record.source ? "text-center fw-bold" : ""}>{record.source || "-"}</div>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Preferred Language"
            field="preferred_language"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "preferred_language",
        key: "preferred_language",
        render: (text: string, record: Lead) => {
          const lang = languageOptions.find((l) => l.value === record.preferred_language);
          const display = lang?.label || record.preferred_language;
          return <div className={!display ? "text-center fw-bold" : ""}>{display || "-"}</div>;
        },
      },
      {
        title: (
          <SortableHeader
            title="Time Zone"
            field="time_zone"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "time_zone",
        key: "time_zone",
        render: (text: string, record: Lead) => (
          <div className={!record.time_zone ? "text-center fw-bold" : ""}>{record.time_zone || "-"}</div>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Currency"
            field="currency_code"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "currency_code",
        key: "currency_code",
        render: (text: string, record: Lead) => (
          <div className={!record.currency_code ? "text-center fw-bold" : ""}>{record.currency_code || "-"}</div>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Priority"
            field="priority"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "priority",
        key: "priority",
        render: (priority: string, record: Lead) => {
          const p = (record as any).priority;
          const getPriorityIcon = (val: string) => {
            switch (val?.toLowerCase()) {
              case "high":
                return <i className="ti ti-square-rounded-filled me-1 text-danger circle" />;
              case "medium":
                return <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />;
              case "low":
                return <i className="ti ti-square-rounded-filled me-1 text-info circle" />;
              default:
                return <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />;
            }
          };
          if (!p) return <div className="text-center">-</div>;
          return (
            <span className="text-default">
              {getPriorityIcon(p)}
              {p}
            </span>
          );
        },
      },
      {
        title: (
          <SortableHeader
            title="Contact Name"
            field="primary_contact"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "primary_contact",
        key: "primary_contact",
        render: (_: any, record: Lead) => (
          <div>
            {record.primary_contact?.full_name && (
              <div>{record.primary_contact.full_name}</div>
            )}
            <span className="text-muted small">
              {record.primary_contact?.primary_phone || "-"}
            </span>
          </div>
        ),
      },
    ],
    [filters.sortBy, filters.sortOrder, handleSortChange]
  );

  const buildLeadSections = useCallback(
    (lead: Lead): EntityDetailSection[] => {
      const lang = languageOptions.find(
        (l) => l.value === lead.preferred_language
      );
      return [
        {
          title: `${getLifecycleTypeName("LEAD")} Info`,
          icon: "ti ti-target-arrow",
          fields: [
            { label: "Title", value: lead.title, fullWidth: true },
            {
              label: "Source",
              value:
                lead.lifecycle_source?.lifecycle_source_display_name ||
                lead.source,
            },
            {
              label: "Status",
              value: lead.lifecycle_type_stage?.stage_name,
            },
            {
              label: "Active",
              value:
                (lead as any).is_active === undefined ||
                  (lead as any).is_active === null
                  ? null
                  : (lead as any).is_active
                    ? "Active"
                    : (lead as any).lifecycle_status_reason?.reason_name ||
                    "Inactive",
            },
            {
              label: "Priority",
              value: <PriorityBadge priority={(lead as any).priority} />,
            },
          ],
        },
        {
          title: "Company & Contact",
          icon: "ti ti-building",
          fields: [
            { label: "Company", value: lead.company?.company_name },
            {
              label: "Contact Name",
              value: lead.primary_contact?.full_name,
            },
            {
              label: "Contact Phone",
              value: lead.primary_contact?.primary_phone,
            },
          ],
        },
        {
          title: "Ownership",
          icon: "ti ti-users",
          fields: [
            {
              label: "Assigned Team",
              value:
                (lead as any).assigned_team?.team_name ||
                (lead as any).assigned_team?.name,
            },
            {
              label: "Assigned User",
              value: lead.assigned_user?.full_name,
            },
          ],
        },
        {
          title: "Deal Details",
          icon: "ti ti-currency-dollar",
          fields: [
            {
              label: "Estimated Value",
              value: lead.estimated_value
                ? `${lead.currency_code || "$"} ${Number(
                  lead.estimated_value || 0
                ).toFixed(4)}`
                : null,
            },
            { label: "Currency", value: lead.currency_code },
            { label: "Preferred Language", value: lang?.label || lead.preferred_language },
            { label: "Time Zone", value: lead.time_zone },
          ],
        },
        {
          title: "Timestamps",
          icon: "ti ti-clock",
          fields: [
            {
              label: "Created",
              value: (lead as any).created_datetime
                ? formatCustomDateTime((lead as any).created_datetime)
                : null,
            },
            {
              label: "Updated",
              value: (lead as any).updated_datetime
                ? formatCustomDateTime((lead as any).updated_datetime)
                : null,
            },
          ],
        },
      ];
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              id="select-all"
              checked={!!selectAll}
              onChange={handleSelectAll}
            />
          </div>
        ),
        dataIndex: "checkbox",
        render: (text: string, record: Lead) => (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              id={`select-${record.lead_id}`}
              checked={
                record.lead_id ? selectedItems.includes(record.lead_id) : false
              }
              onChange={() =>
                record.lead_id && handleSelectItem(record.lead_id)
              }
            />
          </div>
        ),
      },
      ...getSortedAvailableColumns()
        .filter((col) => columnVisibility[col.key])
        .map((col) =>
          allAvailableColumns.find((allCol) => allCol.key === col.key)
        )
        .filter(Boolean),
      {
        title: "Actions",
        dataIndex: "actions",
        render: (text: string, record: Lead) => (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Link
              to="#"
              className="action-icon"
              onClick={(e) => {
                e.preventDefault();
                setDetailsLead(record);
              }}
            >
              <i className="ti ti-eye text-blue-light"></i>
            </Link>
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
              >
                <i className="fa fa-ellipsis-v"></i>
              </Link>
              <div className="dropdown-menu dropdown-menu-end">
                <Link
                  className="dropdown-item"
                  to={`/leads/${record.lead_id}`}
                  state={{ leadData: record }}
                >
                  <i className="ti ti-info-circle text-blue-light"></i> View Details
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_edit_lead"
                  onClick={() => flushSync(() => setSelectedLead(record))}
                >
                  <i className="ti ti-edit text-blue"></i> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={() => handleDelete(record)}
                >
                  <i className="ti ti-trash text-danger"></i> Delete
                </Link>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [
      columnVisibility,
      selectedItems,
      selectAll,
      handleSelectAll,
      handleSelectItem,
      getSortedAvailableColumns,
      allAvailableColumns,
      handleDelete,
      setSelectedLead,
    ]
  );
  return (
    <LeadFormDataProvider isMainApiCompleted={isInitialized} shouldLoad={isAddLeadOpen || !!selectedLead}>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  {" "}
                  <div className="col-8">
                    <h4 className="page-title">
                      {getLifecycleTypePluralName("LEAD")}
                      <span className="count-title">
                        {pagination.totalCount}
                      </span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <ListFilterChips
                        entityType="lead"
                        filters={filters}
                        onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                        onClearAll={() => applyListFilters(getClearedListFilters())}
                        disabled={loading}
                        className="mb-0"
                      />
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        {selectedItems.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger ms-3"
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                          >
                            <i className="ti ti-trash me-2" />
                            Delete Selected ({selectedItems.length})
                          </button>
                        )}{" "}
                        &nbsp; &nbsp;
                        <ExportComponent entityType="lead" filters={filters} />
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add_lead"
                          onClick={() => setIsAddLeadOpen(true)}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add {getLifecycleTypeName("LEAD")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Filter Bar */}
                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={leads}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      sortBy={filters.sort_by}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <SortByDropdown
                      value={filters.sort_by}
                      onChange={(sortBy) =>
                        setFilters((prev) => ({ ...prev, sort_by: sortBy }))
                      }
                    />
                    <span className="mx-2">
                      <LifecycleFilterDropdown
                        entityType="lead"
                        onFilterChange={handleDropdownFilterChange}
                        currentFilters={filters}
                        loading={loading}
                      />
                    </span>
                    <ManageColumns
                      availableColumns={availableColumns}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={handleColumnVisibilityChange}
                      onResetColumns={handleResetColumns}
                      onReorderColumns={setOrderedAvailableColumns}
                    />
                    <div className="view-icons ms-3">
                      <Link to="#" className="active">
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch?.("kanban");
                        }}
                      >
                        <i className="ti ti-layout-kanban" />
                      </Link>
                    </div>
                  </div>
                </div>
                {loading || tenantLoading ? (
                  <div className="card-body">
                    <div
                      className="d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: "60vh" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading {getLifecycleTypePluralName("LEAD")}...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div
                      className="table-responsive contact-table-wrapper"
                      style={{ position: "relative" }}
                    >
                      {paginatedLeads.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                            <i className="ti ti-chart-arcs fs-36 text-gray"></i>
                          </div>
                          <h4 className="text-gray mb-2">
                            No {getLifecycleTypePluralName("LEAD")} Found!
                          </h4>
                          <p className="text-muted mb-3">
                            There are no {getLifecycleTypePluralName("LEAD")} to
                            display at the moment.
                          </p>
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add_lead"
                            onClick={() => setIsAddLeadOpen(true)}
                          >
                            <i className="fa fa-plus me-2" />
                            Add Your First {getLifecycleTypeName("LEAD")}
                          </button>
                        </div>
                      ) : (
                        <>
                          <Table
                            columns={columns}
                            dataSource={paginatedLeads}
                            rowKey={(record: Lead) => record.lead_id || 0}
                            className="table table-striped contact-table"
                            pagination={false}
                            rowSelection={false}
                          />
                          <TablePagination
                            currentPage={pagination.currentPage || currentPage}
                            pageSize={12}
                            totalItems={pagination.totalCount}
                            filteredItems={pagination.totalCount}
                            isFiltered={
                              filters.searchTerm !== "" ||
                              filters.dateRange[0] !== null ||
                              filters.dateRange[1] !== null
                            }
                            onPageChange={handlePageChange}
                            onPageSizeChange={() => { }}
                            entityName={getLifecycleTypePluralName("LEAD")}
                            loading={loading}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Lead Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        id="offcanvas_add_lead"
        tabIndex={-1}
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Add New {getLifecycleTypeName("LEAD")}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => setSelectedLead(null)}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isAddLeadOpen && (
            <LeadForm
              onSubmit={handleCreate}
              onCancel={() => {
                closeOffcanvas("offcanvas_add_lead");
              }}
              isSubmitting={isCreatingLead}
            />
          )}
        </div>
      </div>

      {/* Edit Lead Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        id="offcanvas_edit_lead"
        tabIndex={-1}
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit {getLifecycleTypeName("LEAD")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {selectedLead && (
            <LeadForm
              lead={selectedLead}
              onSubmit={(formData) => {
                if (selectedLead.lead_id) {
                  return handleUpdate(selectedLead.lead_id, formData);
                }
                return Promise.resolve();
              }}
              onCancel={() => {
                closeOffcanvas("offcanvas_edit_lead");
              }}
              isMainApiCompleted={isInitialized}
              isSubmitting={isUpdatingLead}
            />
          )}
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}

      {detailsLead && (
        <EntityDetailsModal
          isOpen={!!detailsLead}
          onClose={() => setDetailsLead(null)}
          title={detailsLead.title}
          subtitle={detailsLead.company?.company_name || undefined}
          eyebrow={getLifecycleTypeName("LEAD")}
          avatarIcon="ti ti-chart-arcs"
          badge={{
            label: detailsLead.lifecycle_type_stage?.stage_name || "No Stage",
            tone: "info",
          }}
          sections={buildLeadSections(detailsLead)}
          onEdit={() => {
            const lead = detailsLead;
            setDetailsLead(null);
            flushSync(() => setSelectedLead(lead));
            openOffcanvas("offcanvas_edit_lead");
          }}
        />
      )}

      {/* Lead Preview Modal */}
      <Modal
        show={activityToggle}
        onHide={() => dispatch(setActivityTogglePopup(false))}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Lead Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLead && (
            <div>
              {/* Lead Header */}
              <div className="d-flex align-items-center mb-4">
                <div className="avatar avatar-lg me-3 flex-shrink-0">
                  <ImageWithBasePath
                    src="assets/img/profiles/avatar-01.jpg"
                    alt="Lead"
                    className="rounded-circle"
                  />
                </div>
                <div>
                  <h5 className="mb-1">{selectedLead.title}</h5>
                  <p className="mb-1 text-muted">
                    <i className="ti ti-building me-1"></i>
                    {selectedLead.company?.company_name || "No Company"}
                  </p>
                  <span
                    className={`badge ${selectedLead.is_active ? "bg-success" : "bg-danger"
                      }`}
                  >
                    {selectedLead.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <hr />

              <div className="row">
                {/* Lead Information */}
                <div className="col-md-6 mb-3">
                  <h6 className="fw-semibold mb-3">
                    <i className="ti ti-user me-2"></i>Lead Information
                  </h6>
                  <div className="info-list">
                    <div className="mb-2">
                      <small className="text-muted d-block">Title</small>
                      <span>{selectedLead.title}</span>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Source</small>
                      <span>{selectedLead.source || "Not specified"}</span>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Score</small>
                      <span>
                        {selectedLead.score
                          ? `${selectedLead.score}/100`
                          : "Not scored"}
                      </span>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        Estimated Value
                      </small>
                      <span>
                        {selectedLead.estimated_value
                          ? `${selectedLead.currency_code || "$"
                          } ${Number(selectedLead.estimated_value || 0).toFixed(4)}`
                          : "Not specified"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="col-md-6 mb-3">
                  <h6 className="fw-semibold mb-3">
                    <i className="ti ti-info-circle me-2"></i>Additional Details
                  </h6>
                  <div className="info-list">
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        Preferred Language
                      </small>
                      <span>
                        {selectedLead.preferred_language || "Not specified"}
                      </span>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Time Zone</small>
                      <span>{selectedLead.time_zone || "Not specified"}</span>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">
                        Assigned User
                      </small>
                      <span>
                        {selectedLead.assigned_user
                          ? `${selectedLead.assigned_user.full_name}`
                          : "No Assigned User"}
                      </span>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Currency</small>
                      <span>
                        {selectedLead.currency_code || "Not specified"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              {/* Timestamps */}
              <div className="row">
                <div className="col-md-6">
                  <small className="text-muted d-block">Created</small>
                  <span>
                    {selectedLead.created_datetime
                      ? new Date(selectedLead.created_datetime).toLocaleString()
                      : "Not specified"}
                  </span>
                </div>
                <div className="col-md-6">
                  <small className="text-muted d-block">Last Updated</small>
                  <span>
                    {selectedLead.updated_datetime
                      ? new Date(selectedLead.updated_datetime).toLocaleString()
                      : "Not specified"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>{" "}
      </Modal>
    </LeadFormDataProvider>
  );
};

export default LeadList;
